import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Spin } from "antd";
import PropTypes from "prop-types";
import { API } from "api";
import { UTILS } from "utils";
import { SVG_Business } from "svg/SVGs";
import { errorHandler } from "api/api/errorHandler";
import { postDataAsFormData } from "api/api/axiosSetup";
import ImageVideoPicker from "components/image-video-picker";
import AppModal from "components/ui/AppModal";
import AppInput from "components/ui/AppInput";
import AppTextArea from "components/ui/AppTextArea";
import AppButton from "components/ui/AppButton";
import AppOutlineButton from "components/ui/AppOutlineButton";

const AddCategory = ({ open, menuId, handleValues, onClose , mode = "add" , cat}) => {
  const { t } = useTranslation();
  const [category, setCategory] = useState(UTILS.getCategoryModel());
  const [loading, setLoading] = useState(false);


  const isErrors = useMemo(() => {
    return Object.values(category?.errors).some((error) => error && error !== "");
  }, [category?.values]);

  const validateInput = ({ value, key, isRequired = true, rules = [] }) => {
    let errors = category?.errors;
    if (!value || (UTILS.hasWhiteSpace(value) && isRequired)) {
      errors[key] = t("required field is required", { field: key });
    }
    if (rules.length > 0) {
      rules.forEach((rule) => {
        if (rule.maxLength && value?.length > rule?.maxLength) {
          errors[key] = t("field should not exceed {maxLength} characters", {
            maxLength: rule.maxLength,
          });
        }
      });
    }
    return errors;
  };

  const handleMediaChange = (info) => {
    const file = Array.isArray(info) ? info?.[0] : info;
    const error = file?.error?.message;
    setCategory((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        imageUrl: file,
      },
      errors: {
        ...prevState.errors,
        imageUrl: error,
      },
    }));
  };

  const handleInputChange = (key, val, isRequired = true, rules = []) => {
    const errors = validateInput({ value: val, key, isRequired, rules });
    setCategory((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        [key]: val,
      },
      errors: {
        ...prevState.errors,
        ...errors,
      },
    }));
  };

  const handleMediaUpload = async (file) => {
    try {
      const resp = await postDataAsFormData(
        "fms/upload-media-files",
        { media: file, preventCache: true },
        ["media"]
      );
      return resp?.data;
    } catch (error) {
      console.log(error);
    }
  };

  const saveCategory = async () => {
    try {
      if (isErrors) {
        UTILS.errorNotification(t("Please fill all the required fields"));
        return;
      }

      const userDetail = UTILS.getCurrentUser();
      setLoading(true);
      let imageUrl = category?.values?.imageUrl;
      if(imageUrl && imageUrl?.originFileObj) {
        // imageUrl = await UTILS.getBase64(imageUrl?.originFileObj);
        imageUrl = await handleMediaUpload(imageUrl?.originFileObj);
      }

      const rawValues = {
        baseLanguage: { name: category?.values?.name },
        imageUrl: imageUrl,
        description: category?.values?.description,
      }

      const payload = {
        ...rawValues,
        brandId: userDetail.brandId,
        menuId: menuId,
      }
      if(mode === "edit") {
        payload.categoryId = cat?._id;
      }

      const result = mode === "add" ? await API.ADD_CATEGORY(payload) : await API.UPDATE_CATEGORY(payload);

      if (result?.success === 1) {
        const categoryId = result.data?.category?._id;
        const obj = {
          categoryId: {
            _id: categoryId,
           ...rawValues,
          },
          items: [],
        };
        handleValues(mode === "add" ? obj : {...rawValues, _id: cat?._id} );
        UTILS.successNotification(result?.message || t(mode === "add" ? "Category added" : "Category updated"));
      } else {
        UTILS.errorNotification(errorHandler(result?.message));
      }
    } catch (error) {
      UTILS.errorNotification(errorHandler(error?.error?.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {

    if(mode === "edit") {
    setCategory((prevState) => ({
      ...prevState,
      values: {
        name: cat?.baseLanguage?.name,
        description: cat?.description,
        imageUrl: cat?.imageUrl,
        _id: cat?._id,
      },
      errors: prevState.errors,
    }));
  }
  }, [cat, mode]);

  const isUnchangedInEditMode =
    mode === "edit" && JSON.stringify(category?.values) === JSON.stringify(cat);

  const footer = (
    <div className="flex justify-end gap-3">
      <AppOutlineButton
        onClick={(e) => {
          e?.preventDefault?.();
          e?.stopPropagation?.();
          onClose();
        }}
        disabled={loading}
      >
        {t("Cancel")}
      </AppOutlineButton>
      <AppButton
        onClick={saveCategory}
        loading={loading}
        disabled={loading || isErrors || isUnchangedInEditMode}
      >
        {t(mode === "add" ? "Create" : "Update")}
      </AppButton>
    </div>
  );

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={t(mode === "add" ? "Add New Category" : "Edit Category")}
      width={720}
      footer={footer}
      maskClosable={false}
    >
      <Spin spinning={loading}>
        <div className="p-2">
          <div className="mb-3 flex justify-center">
            <SVG_Business />
          </div>
          <div className="w-full space-y-4">
            <AppInput
              label={t("Name")}
              placeholder={t("Example watches")}
              value={category?.values?.name}
              onInput={(e) => handleInputChange("name", e?.target?.value)}
              error={category?.errors?.name}
              mandatory
            />
            <AppTextArea
              label={t("Description")}
              placeholder={t("Description")}
              value={category?.values?.description}
              maxLength={150}
              showCount
              onInput={(e) => {
                handleInputChange("description", e?.target?.value, false, [
                  { maxLength: 150 },
                ]);
              }}
              error={category?.errors?.description}
            />
            <ImageVideoPicker
              label={"Upload Category Image"}
              info={{
                text: "Category Image",
                props: {
                  maxSizeMB: 2,
                  dimensions: "150x150",
                },
              }}
              validationRules={{
                image: {
                  maxSizeMB: 2
                },
              }}
              value={category?.values?.imageUrl}
              onChange={handleMediaChange}
            />
          </div>
        </div>
      </Spin>
    </AppModal>
  );
};

AddCategory.propTypes = {
  open: PropTypes.bool,
  handleValues: PropTypes.func,
  onClose: PropTypes.func,
};
export default AddCategory;
